/**
 * Activity / History family icon-and-color system + actor / signed-impact helpers.
 *
 * Pure, presentational mapping over the normalized `ActivityEvent` rows. Replaces
 * the prototype's fabricated `AUDIT_EVENT_COLORS` / `TRANSACTION_TYPE_COLORS`.
 *
 * Honesty rules (locked by the backend, enforced here):
 *  - event titles come from EVENT_TYPE_LABELS keyed by the model-backed `event_type`,
 *    NEVER parsed from the free-text `description` (ACT-01).
 *  - blank / unknown actor → "unknown"; automatic mark → "System (automatic)"; never a
 *    fabricated name and never a silent "System" default (ACT-04 / T-12-01).
 *  - signed impact renders the backend Decimal STRING verbatim (sign/abs via string ops
 *    only, no parseFloat arithmetic) (HON-03 / T-12-02). The word "P&L" never appears.
 */
import type { ComponentType, SVGProps } from 'react';
import {
  ArrowsRightLeftIcon,
  ChartBarIcon,
  BanknotesIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import type {
  ActivityEvent,
  ActivityEventType,
  ActivityFamily,
  ActivityImpact,
} from '@/types';

type HeroIcon = ComponentType<SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;

export interface FamilyMeta {
  /** Timeline dot color (the deliberate family hue). */
  dot: string;
  /** Faint family pill classes (bg + text). */
  pill: string;
  /** Human family label. */
  label: string;
  /** Heroicon component reference (rendered in 12-02). */
  Icon: HeroIcon;
}

/** ADR §1 family → dot color + faint pill + label + glyph (UI-SPEC family table). */
export const FAMILY_META: Record<ActivityFamily, FamilyMeta> = {
  lifecycle: {
    dot: 'bg-blue-500',
    pill: 'bg-blue-50 text-blue-700',
    label: 'Lifecycle',
    Icon: ArrowsRightLeftIcon,
  },
  valuation: {
    dot: 'bg-cyan-500',
    pill: 'bg-cyan-50 text-cyan-700',
    label: 'Valuation',
    Icon: ChartBarIcon,
  },
  accounting: {
    dot: 'bg-emerald-500',
    pill: 'bg-emerald-50 text-emerald-700',
    label: 'Accounting',
    Icon: BanknotesIcon,
  },
  corporate: {
    dot: 'bg-violet-500',
    pill: 'bg-violet-50 text-violet-700',
    label: 'Corporate',
    Icon: SparklesIcon,
  },
};

/** Event types whose dot renders in the red terminal-adverse variant. */
const TERMINAL_ADVERSE: ReadonlySet<ActivityEventType> = new Set(['terminated']);

/**
 * Timeline dot color for an event: red for terminated (terminal-adverse),
 * otherwise the family dot. Matured / Closed keep the family dot (the gray ring is a
 * 12-02 view concern, not a color change). Falls back to the `lifecycle` family on
 * an unrecognised value so a malformed / future row never crashes the tab with
 * "Cannot read property 'dot' of undefined" (LR-208 r5 L8).
 */
export function resolveDotColor(event: ActivityEvent): string {
  if (TERMINAL_ADVERSE.has(event.event_type)) {
    return 'bg-red-500';
  }
  return (FAMILY_META[event.family] ?? FAMILY_META.lifecycle).dot;
}

/** Human event titles — sourced from the model-backed `event_type`, NEVER `description` (ACT-01). */
export const EVENT_TYPE_LABELS: Record<ActivityEventType, string> = {
  booked: 'Booked',
  amended: 'Amended',
  open_settlement: 'Open settlement',
  close_settlement: 'Close settlement',
  hold: 'Open settlement — hold placed',
  transfer: 'Open settlement — securities transferred',
  release: 'Open settlement — hold released',
  settled_cash: 'Cash settled',
  settled_securities: 'Securities settled',
  notice: 'Notice given',
  marked: 'Marked to market',
  mtm_transfer: 'MTM cash transfer',
  re_rated: 'Re-rated',
  terminated: 'Terminated (early)',
  // LR-576 (feedback п.70): the event stamps the CLOSE date — label in sync
  // with the "Set Close Date" action rename.
  set_settlement_date: 'Close date set',
  matured: 'Matured',
  closed: 'Closed',
};

/**
 * Whether an impact's `currency` is a real fiat (cash) code rather than a
 * security symbol_id. The securities leg of a settlement carries the symbol_id
 * (e.g. "AAPL.NASDAQ") in `currency`; only an ISO-4217-style code (three
 * uppercase letters: USD, EUR, GBP, HKD…) is cash. A "ticker.exchange" symbol
 * is the share leg and must never read as cash (F7).
 */
function isFiatCurrency(currency: string | null): boolean {
  return !!currency && /^[A-Z]{3}$/.test(currency.trim());
}

/**
 * Whether an impact carries a non-zero CASH amount (a fiat currency + a non-zero
 * Decimal string). The securities leg of a settlement carries no cash impact
 * (a symbol_id "currency" or a null/zero amount), so this distinguishes the
 * cash-collateral leg from the share leg. String-only zero check (mirrors
 * formatSignedImpact) — no float math (HON-03).
 */
function hasCashImpact(impact: ActivityImpact): boolean {
  const { amount, currency } = impact;
  if (!isFiatCurrency(currency)) return false;
  if (amount === null || amount === '' || /^[+-]?0*(\.0+)?$/.test(amount.trim())) return false;
  return true;
}

/**
 * Human event title, currency-aware for the open-settlement `transfer` event:
 * the same event_type covers both the securities leg and the cash-collateral
 * leg, distinguished only by the model-backed cash impact (NEVER the free-text
 * description — ACT-01). A cash leg reads "cash transferred", not "securities".
 */
export function resolveEventLabel(event: ActivityEvent): string {
  if (event.event_type === 'transfer' && hasCashImpact(event.impact)) {
    return 'Open settlement — cash transferred';
  }
  return EVENT_TYPE_LABELS[event.event_type];
}

/** Short leg labels for the children of a settlement parent (the "Open/Close
 * settlement — " prefix is redundant once nested under that parent). The cash
 * vs securities split is driven by the model-backed cash impact, never the
 * free-text description (ACT-01). */
const LEG_LABELS: Partial<Record<ActivityEventType, string>> = {
  hold: 'Hold placed',
  transfer: 'Securities transferred',
  release: 'Hold released',
};

export function resolveLegLabel(event: ActivityEvent): string {
  if (event.event_type === 'transfer' && hasCashImpact(event.impact)) {
    return 'Cash transferred';
  }
  return LEG_LABELS[event.event_type] ?? resolveEventLabel(event);
}

/**
 * Honest actor display.
 *  - user with a real name → the name
 *  - system → "System (automatic)"
 *  - backoffice → "back-office"
 *  - counterparty → name || "counterparty"
 *  - unknown OR (name null/blank) → "unknown"
 * Never fabricates a name; never defaults a blank actor to "System".
 */
export function resolveActor(event: ActivityEvent): string {
  const { kind, name } = event.actor;
  const trimmed = name?.trim() ?? '';

  switch (kind) {
    case 'system':
      return 'System (automatic)';
    case 'backoffice':
      return 'back-office';
    case 'counterparty':
      return trimmed || 'counterparty';
    case 'user':
      return trimmed || 'unknown';
    case 'unknown':
    default:
      return 'unknown';
  }
}

export interface SignedImpact {
  /** Display string with sign prefix + verbatim amount + currency (or em dash). */
  text: string;
  tone: 'pos' | 'neg' | 'none';
  /** Direction-of-cash caption — NOT profit/loss; "P&L" never used. */
  caption: string;
}

const EM_DASH = '—';
const MINUS = '−';

/**
 * Format a signed cash impact from the backend Decimal STRING.
 *  - null / empty / numerically-zero amount → em dash, no caption (a zero cash impact is
 *    neither "due to us" nor "due to CP"; never fabricate a direction — WR-01, never a
 *    fabricated $0.00 either)
 *  - negative (leading '-') → "−{abs}{ ccy}", neg tone, "due to CP"
 *  - otherwise → "+{amount}{ ccy}", pos tone, "due to us"
 * The amount portion is rendered verbatim — only sign/abs and the zero check handled by
 * string ops, NO parseFloat arithmetic (HON-03). The word "P&L" never appears.
 */
export function formatSignedImpact(impact: ActivityImpact): SignedImpact {
  const { amount, currency } = impact;
  // Treat null, empty, or a numerically-zero string (e.g. "0", "0.00", "-0.00000000")
  // as the none case — a zero impact asserts no cash direction (WR-01). String-only
  // check (optional sign, all-zero integer part, optional all-zero fractional part).
  if (amount === null || amount === '' || /^[+-]?0*(\.0+)?$/.test(amount.trim())) {
    return { text: EM_DASH, tone: 'none', caption: '' };
  }

  const isNeg = amount.trimStart().startsWith('-');
  const abs = isNeg ? amount.replace('-', '') : amount;
  const ccy = currency ? ` ${currency}` : '';
  // "due to us / due to CP" is a CASH receivable/payable direction. A securities
  // leg carries a symbol_id "currency" (e.g. "AAPL.NASDAQ") and moves shares, not
  // cash — never assert a cash direction on it (F7 / WR-01). Keep the signed
  // quantity, drop the caption.
  const isCash = isFiatCurrency(currency);

  if (isNeg) {
    return { text: `${MINUS}${abs}${ccy}`, tone: 'neg', caption: isCash ? 'due to CP' : '' };
  }
  return { text: `+${abs}${ccy}`, tone: 'pos', caption: isCash ? 'due to us' : '' };
}
